/**
 * outlive-cart-drawer.js — Cart drawer leve para Shopify Horizon
 *
 * Recursos:
 * - Drawer leve independente do cart-drawer nativo do Horizon
 * - Compatível com botão do header #hz-cart-btn
 * - Compatível com widget de assinatura que chama #hz-cart-drawer.open()
 * - Sincroniza brindes da assinatura
 * - Bloqueia remoção/alteração dos brindes
 * - Mostra desconto aplicado no carrinho
 * - Mostra descontos por item quando disponíveis no /cart.js
 * - Mostra upsells por produto usando dados vindos do snippet Liquid
 */

(function () {
  'use strict';

  if (window.__OutliveCartDrawerLoaded) return;
  window.__OutliveCartDrawerLoaded = true;

  const CONFIG = {
    drawerSelector: '#hz-cart-drawer',
    checkoutUrl: '/checkout',

    standardGiftVariantIds: [
      41668987682910,
      41668986863710,
      42223230910558,
    ],

    extraGiftVariantId: 42283245174878,

    subscriptionPrices: [
      160.87, 178.74, 185.94, 191.94,
      197.94, 255.91, 255.92, 280.42,
      253.22, 263.42, 271.92, 280.42,
      268.11, 278.91, 287.91, 296.91,
      297.9, 309.9, 319.9, 329.9,
      297.8, 309.8, 319.8, 329.8,
    ],

    extraGiftPrices: [
      297.8,
      309.8,
      319.8,
      329.8,
    ],

    messages: {
      standard: 'Nesse mês de aniversário, ao incluir a assinatura, você ganhou 3 brindes: Scoop, Mixer e Travel Box!',
      extra: 'Ao incluir a assinatura, você ganhou 3 brindes: Scoop, Mixer e mais 1 brinde exclusivo!',
    },
  };

  const nativeFetch = window.fetch.bind(window);
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function money(cents) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format((Number(cents) || 0) / 100);
  }

  function centsToBRL(cents) {
    return Math.round(Number(cents) || 0) / 100;
  }

  function closeEnough(a, b) {
    return Math.abs(Number(a) - Number(b)) < 0.02;
  }

  function isGiftVariant(id) {
    const n = Number(id);
    return CONFIG.standardGiftVariantIds.includes(n) || n === Number(CONFIG.extraGiftVariantId);
  }

  function isSubPrice(brl) {
    return CONFIG.subscriptionPrices.some((p) => closeEnough(p, brl));
  }

  function isExtraPrice(brl) {
    return CONFIG.extraGiftPrices.some((p) => closeEnough(p, brl));
  }

  function isSubscriptionItem(item) {
    if (!item || isGiftVariant(item.variant_id)) return false;
    return isSubPrice(centsToBRL(item.price));
  }

  function hasExtraGiftTrigger(cart) {
    return (cart.items || []).some((item) => {
      if (!isSubscriptionItem(item)) return false;
      return isExtraPrice(centsToBRL(item.price));
    });
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function getUpsellData() {
    const script = document.getElementById('outlive-cart-upsell-data');
    if (!script) return {};

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('[OutliveCartDrawer] Erro ao ler upsells:', error);
      return {};
    }
  }

  function getCartDiscountLabels(cart) {
    const labels = [];

    (cart.cart_level_discount_applications || []).forEach((discount) => {
      if (discount?.title) labels.push(discount.title);
    });

    (cart.discount_applications || []).forEach((discount) => {
      if (discount?.title) labels.push(discount.title);
    });

    return [...new Set(labels.filter(Boolean))];
  }

  function getItemDiscountLabels(item) {
    const labels = [];

    (item.line_level_discount_allocations || []).forEach((allocation) => {
      if (allocation?.discount_application?.title) {
        labels.push(allocation.discount_application.title);
      }
    });

    (item.discounts || []).forEach((discount) => {
      if (discount?.title) labels.push(discount.title);
    });

    return [...new Set(labels.filter(Boolean))];
  }

  async function fetchCart() {
    const res = await nativeFetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    });

    if (!res.ok) throw new Error('Erro ao carregar carrinho.');
    return res.json();
  }

  async function addItems(items) {
    const res = await nativeFetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items }),
    });

    if (!res.ok) throw new Error('Erro ao adicionar item.');
    return res.json();
  }

  async function updateItems(updates) {
    const res = await nativeFetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });

    if (!res.ok) throw new Error('Erro ao atualizar carrinho.');
    return res.json();
  }

  async function changeLine(line, quantity) {
    const res = await nativeFetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        line,
        quantity,
      }),
    });

    if (!res.ok) throw new Error('Erro ao alterar item.');
    return res.json();
  }

  function syncBubble(count) {
    const itemCount = Number(count) || 0;

    document.querySelectorAll('[ref="cartBubbleCount"]').forEach((el) => {
      el.textContent = itemCount > 0 ? String(itemCount) : '';
      el.classList.toggle('hidden', itemCount === 0);
    });

    document.querySelectorAll('[ref="cartBubble"]').forEach((el) => {
      el.classList.toggle('visually-hidden', itemCount === 0);

      if (itemCount <= 99) {
        el.setAttribute('data-maintain-ratio', '');
      } else {
        el.removeAttribute('data-maintain-ratio');
      }
    });

    document.querySelectorAll('.header-actions__cart-icon').forEach((el) => {
      el.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    });
  }

  function expectedGiftsMap(cartItems) {
    let subscriptionQty = 0;
    let hasExtra = false;

    (cartItems || []).forEach((item) => {
      if (!isSubscriptionItem(item)) return;

      subscriptionQty += Number(item.quantity) || 0;

      if (isExtraPrice(centsToBRL(item.price))) {
        hasExtra = true;
      }
    });

    const expected = new Map();

    if (subscriptionQty <= 0) return expected;

    CONFIG.standardGiftVariantIds.forEach((id) => {
      expected.set(Number(id), subscriptionQty);
    });

    if (hasExtra) {
      expected.set(Number(CONFIG.extraGiftVariantId), subscriptionQty);
    }

    return expected;
  }

  let syncingGifts = false;
  let pendingSync = null;

  async function syncGifts(cartData = null) {
    if (syncingGifts) return cartData || fetchCart();

    syncingGifts = true;

    try {
      let cart = cartData || await fetchCart();

      const expected = expectedGiftsMap(cart.items);
      const current = new Map();

      (cart.items || []).forEach((item) => {
        if (isGiftVariant(item.variant_id)) {
          current.set(Number(item.variant_id), Number(item.quantity) || 0);
        }
      });

      const updates = {};
      const toAdd = [];

      expected.forEach((qty, variantId) => {
        if (!current.has(variantId)) {
          toAdd.push({
            id: variantId,
            quantity: qty,
          });
        } else if (current.get(variantId) !== qty) {
          updates[variantId] = qty;
        }
      });

      current.forEach((qty, variantId) => {
        if (!expected.has(variantId)) {
          updates[variantId] = 0;
        }
      });

      if (Object.keys(updates).length) {
        await updateItems(updates);
        cart = await fetchCart();
      }

      if (toAdd.length) {
        await addItems(toAdd);
        cart = await fetchCart();
      }

      syncBubble(cart.item_count);

      return cart;
    } finally {
      syncingGifts = false;
    }
  }

  function runGiftSync(cartData = null) {
    const promise = syncGifts(cartData).catch(() => cartData || null);

    pendingSync = promise.finally(() => {
      if (pendingSync === promise) pendingSync = null;
    });

    return promise;
  }

  async function waitForGiftSync() {
    const startedAt = Date.now();

    if (pendingSync) {
      await Promise.race([
        pendingSync,
        delay(2000),
      ]);
    }

    while (syncingGifts && Date.now() - startedAt < 2000) {
      await delay(50);
    }
  }

  class OutliveCartDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-ocd-overlay]');
      this.panel = this.querySelector('[data-ocd-panel]');
      this.content = this.querySelector('[data-ocd-content]');
      this.footer = this.querySelector('[data-ocd-footer]');
      this.empty = this.querySelector('[data-ocd-empty]');
      this.loading = this.querySelector('[data-ocd-loading]');
      this.errorBox = this.querySelector('[data-ocd-error]');
      this.subtotal = this.querySelector('[data-ocd-subtotal]');
      this.count = this.querySelector('[data-ocd-count]');

      this.addEventListener('click', this.onClick.bind(this));

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.hasAttribute('open')) {
          this.close();
        }
      });

      fetchCart()
        .then((cart) => {
          syncBubble(cart.item_count);
          this.render(cart);
        })
        .catch(() => {});
    }

    async open() {
      this.setAttribute('open', '');
      document.documentElement.classList.add('outlive-cart-drawer-is-open');
      document.body.classList.add('outlive-cart-drawer-is-open');

      this.setLoading(true);
      this.clearError();

      try {
        let cart = await fetchCart();
        cart = await syncGifts(cart);
        this.render(cart);
      } catch (error) {
        this.showError('Não foi possível carregar o carrinho. Tente novamente.');
      } finally {
        this.setLoading(false);
      }
    }

    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('outlive-cart-drawer-is-open');
      document.body.classList.remove('outlive-cart-drawer-is-open');
    }

    setLoading(isLoading) {
      this.toggleAttribute('loading', isLoading);

      if (this.loading) {
        this.loading.hidden = !isLoading;
      }
    }

    showError(message) {
      if (!this.errorBox) return;

      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }

    clearError() {
      if (!this.errorBox) return;

      this.errorBox.textContent = '';
      this.errorBox.hidden = true;
    }

    async onClick(event) {
      const closeBtn = event.target.closest('[data-ocd-close]');
      const checkoutBtn = event.target.closest('[data-ocd-checkout]');
      const quantityBtn = event.target.closest('[data-ocd-quantity]');
      const removeBtn = event.target.closest('[data-ocd-remove]');
      const upsellBtn = event.target.closest('[data-ocd-upsell-add]');

      if (closeBtn || event.target === this.overlay) {
        event.preventDefault();
        this.close();
        return;
      }

      if (checkoutBtn) {
        event.preventDefault();

        checkoutBtn.disabled = true;

        try {
          await waitForGiftSync();
          window.location.href = CONFIG.checkoutUrl;
        } finally {
          checkoutBtn.disabled = false;
        }

        return;
      }

      if (upsellBtn) {
        event.preventDefault();

        const variantId = upsellBtn.dataset.variantId;
        if (!variantId) return;

        await this.addUpsell(variantId, upsellBtn);
        return;
      }

      if (quantityBtn) {
        event.preventDefault();

        const line = Number(quantityBtn.dataset.line);
        const qty = Number(quantityBtn.dataset.quantity);

        if (!line || qty < 0) return;

        await this.updateLine(line, qty);
        return;
      }

      if (removeBtn) {
        event.preventDefault();

        const line = Number(removeBtn.dataset.line);

        if (!line) return;

        await this.updateLine(line, 0);
      }
    }

    async updateLine(line, quantity) {
      this.setLoading(true);
      this.clearError();

      try {
        let cart = await changeLine(line, quantity);
        cart = await syncGifts(cart);
        this.render(cart);
      } catch (error) {
        this.showError('Não foi possível atualizar o carrinho. Tente novamente.');
      } finally {
        this.setLoading(false);
      }
    }

  async addUpsell(variantId, button = null) {
  if (!variantId) return;

  const originalText = button ? button.textContent : '';

  if (button) {
    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = '...';
    button.setAttribute('aria-label', 'Adicionando produto');
  }

  this.setLoading(true);
  this.clearError();

  try {
    await addItems([
      {
        id: Number(variantId),
        quantity: 1,
      },
    ]);

    let cart = await fetchCart();
    cart = await syncGifts(cart);

    this.render(cart);
  } catch (error) {
    this.showError('Não foi possível adicionar este produto. Tente novamente.');
  } finally {
    if (button) {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.textContent = originalText || 'Adicionar';
      button.removeAttribute('aria-label');
    }

    this.setLoading(false);
  }
}

    render(cart) {
      const items = cart.items || [];
      const normalItems = items.filter((item) => !isGiftVariant(item.variant_id));
      const giftItems = items.filter((item) => isGiftVariant(item.variant_id));
      const orderedItems = [...normalItems, ...giftItems];
      const hasItems = orderedItems.length > 0;
      const hasSub = normalItems.some(isSubscriptionItem);
      const hasExtra = hasExtraGiftTrigger(cart);
      const cartDiscountLabels = getCartDiscountLabels(cart);

      syncBubble(cart.item_count);

      if (this.count) {
        this.count.textContent = cart.item_count === 1
          ? '1 item'
          : `${cart.item_count || 0} itens`;
      }

      if (this.subtotal) {
        this.subtotal.textContent = money(cart.total_price);
      }

      if (this.empty) {
        this.empty.hidden = hasItems;
      }

      if (this.footer) {
        this.footer.hidden = !hasItems;
      }

      const discountTarget = this.querySelector('[data-ocd-discounts]');

      if (discountTarget) {
        if (cartDiscountLabels.length) {
          discountTarget.hidden = false;
          discountTarget.innerHTML = cartDiscountLabels.map((label) => {
            return `
              <div class="ocd-discount-line">
                <span>Desconto aplicado</span>
                <strong>${escapeHtml(label)}</strong>
              </div>
            `;
          }).join('');
        } else {
          discountTarget.hidden = true;
          discountTarget.innerHTML = '';
        }
      }

      if (!this.content) return;

      if (!hasItems) {
        this.content.innerHTML = '';
        return;
      }

      this.content.innerHTML = `
        ${hasSub ? this.renderGiftMessage(hasExtra ? 'extra' : 'standard') : ''}
        <div class="ocd-items">
          ${orderedItems.map((item) => this.renderItem(item, items.indexOf(item) + 1)).join('')}
        </div>
        ${this.renderUpsells(normalItems)}
      `;
    }

    renderGiftMessage(type) {
      const text = CONFIG.messages[type];

      if (!text) return '';

      return `
        <div class="ocd-gift-message" role="status" aria-live="polite">
          <span class="ocd-gift-message__icon">🎁</span>
          <span>${escapeHtml(text)}</span>
        </div>
      `;
    }

    renderItem(item, line) {
      const isGift = isGiftVariant(item.variant_id);

      const image = item.image
        ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
        : `<div class="ocd-item__placeholder"></div>`;

      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="ocd-item__variant">${escapeHtml(item.variant_title)}</p>`
        : '';

      const sellingPlan = item.selling_plan_allocation?.selling_plan?.name
        ? `<p class="ocd-item__selling-plan">${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</p>`
        : '';

      const discountLabels = getItemDiscountLabels(item);

      const discountsHtml = discountLabels.length
        ? `
          <div class="ocd-item__discounts">
            ${discountLabels.map((label) => `<span>${escapeHtml(label)}</span>`).join('')}
          </div>
        `
        : '';

      const properties = Object.entries(item.properties || {})
        .filter(([key, value]) => value && !String(key).startsWith('_'))
        .map(([key, value]) => {
          return `<p class="ocd-item__property">${escapeHtml(key)}: ${escapeHtml(value)}</p>`;
        })
        .join('');

      const qtyControls = isGift
        ? `<div class="ocd-item__gift-lock">Brinde grátis</div>`
        : `
          <div class="ocd-qty" aria-label="Quantidade">
            <button
              type="button"
              data-ocd-quantity
              data-line="${line}"
              data-quantity="${Math.max(0, item.quantity - 1)}"
              aria-label="Diminuir quantidade"
            >
              −
            </button>

            <span>${item.quantity}</span>

            <button
              type="button"
              data-ocd-quantity
              data-line="${line}"
              data-quantity="${item.quantity + 1}"
              aria-label="Aumentar quantidade"
            >
              +
            </button>
          </div>

          <button
            type="button"
            class="ocd-item__remove"
            data-ocd-remove
            data-line="${line}"
          >
            Remover
          </button>
        `;

      const originalPrice = item.original_line_price && item.original_line_price > item.final_line_price
        ? `<span class="ocd-item__compare">${money(item.original_line_price)}</span>`
        : '';

      const price = isGift
        ? `<span class="ocd-item__free">Grátis</span>`
        : `${originalPrice}<span>${money(item.final_line_price)}</span>`;

      return `
        <article
          class="ocd-item${isGift ? ' ocd-item--gift' : ''}"
          data-line="${line}"
          data-key="${escapeHtml(item.key)}"
        >
          <a class="ocd-item__media" href="${escapeHtml(item.url || '#')}">
            ${image}
          </a>

          <div class="ocd-item__main">
            ${isGift ? '<span class="ocd-item__badge">🎁 Brinde</span>' : ''}

            <a class="ocd-item__title" href="${escapeHtml(item.url || '#')}">
              ${escapeHtml(item.product_title)}
            </a>

            ${variantTitle}
            ${sellingPlan}
            ${discountsHtml}
            ${properties}

            <div class="ocd-item__bottom">
              <div class="ocd-item__actions">
                ${qtyControls}
              </div>

              <div class="ocd-item__price">
                ${price}
              </div>
            </div>
          </div>
        </article>
      `;
    }

    renderUpsells(items) {
      const upsellData = getUpsellData();
      const alreadyInCart = new Set((items || []).map((item) => String(item.handle)));
      const upsells = [];
      const seen = new Set();

      (items || []).forEach((item) => {
        const handle = item.handle;
        const source = upsellData[handle];

        if (!source) return;

        const type = item.selling_plan_allocation ? 'assinatura' : 'avulsa';
        const list = source[type] || [];

        list.forEach((product) => {
          if (!product || !product.id) return;
          if (seen.has(String(product.id))) return;
          if (alreadyInCart.has(String(product.handle))) return;

          seen.add(String(product.id));
          upsells.push(product);
        });
      });

      if (!upsells.length) return '';

      return `
        <section class="ocd-upsells" aria-label="Você também pode gostar">
          <div class="ocd-upsells__header">
            <h3>Você também pode gostar</h3>
          </div>

          <div class="ocd-upsells__track">
            ${upsells.map((product) => this.renderUpsellCard(product)).join('')}
          </div>
        </section>
      `;
    }

    renderUpsellCard(product) {
      const image = product.image
        ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy">`
        : `<div class="ocd-upsell__placeholder"></div>`;

      const disabled = !product.available ? 'disabled' : '';
      const buttonText = product.available ? 'Adicionar' : 'Esgotado';

      return `
        <article class="ocd-upsell">
          <a href="${escapeHtml(product.url || '#')}" class="ocd-upsell__image">
            ${image}
          </a>

          <div class="ocd-upsell__info">
            <a href="${escapeHtml(product.url || '#')}" class="ocd-upsell__title">
              ${escapeHtml(product.title)}
            </a>

            <div class="ocd-upsell__bottom">
              <span class="ocd-upsell__price">
                ${money(product.price)}
              </span>

              <button
                type="button"
                class="ocd-upsell__button"
                data-ocd-upsell-add
                data-variant-id="${escapeHtml(product.id)}"
                ${disabled}
              >
                ${buttonText}
              </button>
            </div>
          </div>
        </article>
      `;
    }
  }

  if (!customElements.get('outlive-cart-drawer')) {
    customElements.define('outlive-cart-drawer', OutliveCartDrawer);
  }

  function getDrawer() {
    return document.querySelector(CONFIG.drawerSelector);
  }

  function openDrawer() {
    const drawer = getDrawer();

    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  function interceptCartLinks() {
    document.addEventListener('click', (event) => {
      const headerCartButton = event.target.closest('#hz-cart-btn');
      const cartLink = event.target.closest('a[href="/cart"], a[href*="/cart?"], [data-cart-drawer-open]');

      if (!headerCartButton && !cartLink) return;

      event.preventDefault();
      openDrawer();
    });
  }

  function patchFetchForCart() {
    window.fetch = async function (input, init, ...rest) {
      const url = typeof input === 'string' ? input : (input?.url || '');
      const method = (init?.method || 'GET').toUpperCase();

      const isCartAdd = method === 'POST' && url.includes('/cart/add');
      const isCartChange = method === 'POST' && (
        url.includes('/cart/change') ||
        url.includes('/cart/update')
      );

      const response = await nativeFetch(input, init, ...rest);

      if (isCartAdd || isCartChange) {
        response.clone().json().then(async (data) => {
          const addedItems = data.items || (data.id ? [data] : []);
          const addedNonGift = addedItems.some((item) => !isGiftVariant(item.variant_id));

          let cart = null;

          if (isCartAdd && addedNonGift) {
            await delay(80);

            cart = await fetchCart();
            cart = await runGiftSync(cart);

            const drawer = getDrawer();

            if (drawer?.hasAttribute('open')) {
              drawer.render(cart);
            }
          }

          if (isCartChange) {
            await delay(80);

            cart = data.items ? data : await fetchCart();

            const hasGiftOrSub = (cart.items || []).some((item) => {
              return isGiftVariant(item.variant_id) || isSubscriptionItem(item);
            });

            cart = hasGiftOrSub ? await runGiftSync(cart) : cart;

            syncBubble(cart.item_count);

            const drawer = getDrawer();

            if (drawer?.hasAttribute('open')) {
              drawer.render(cart);
            }
          }
        }).catch(() => {});
      }

      return response;
    };
  }

  function exposeGlobalApi() {
    window.OutliveCartDrawer = {
      open: openDrawer,

      close: () => {
        getDrawer()?.close?.();
      },

      refresh: async () => {
        const drawer = getDrawer();

        let cart = await fetchCart();
        cart = await syncGifts(cart);

        drawer?.render?.(cart);

        return cart;
      },

      syncGifts,
    };
  }

  function init() {
    interceptCartLinks();
    patchFetchForCart();
    exposeGlobalApi();

    fetchCart()
      .then(async (cart) => {
        const hasGiftOrSub = (cart.items || []).some((item) => {
          return isGiftVariant(item.variant_id) || isSubscriptionItem(item);
        });

        const finalCart = hasGiftOrSub ? await syncGifts(cart) : cart;

        syncBubble(finalCart.item_count);
        getDrawer()?.render?.(finalCart);
      })
      .catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();